import { TextInput, Textarea, Stack, Switch } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useEffect } from "react";
import { IcdCode } from "../../../services/admin/icdcodes.service";
import {
  required,
  compose,
  maxLength,
  scrollToFirstError,
} from "../../../utils/formValidation";

export interface IcdCodeFormValues {
  code: string;
  description: string;
  isActive: boolean;
}

interface IcdCodeFormProps {
  initialValues?: IcdCode;
  onSubmit: (values: IcdCodeFormValues) => void;
}

export default function IcdCodeForm({
  initialValues,
  onSubmit,
}: IcdCodeFormProps) {
  const form = useForm<IcdCodeFormValues>({
    initialValues: {
      code: initialValues?.code || "",
      description: initialValues?.description || "",
      isActive: initialValues?.isActive ?? true,
    },
    validate: {
      code: compose(
        required("ICD Code is required"),
        maxLength(20, "ICD Code must be 20 characters or less")
      ),
      description: required("Description is required"),
    },
  });

  // Reset form when initialValues change (switching between add/edit)
  useEffect(() => {
    form.setValues({
      code: initialValues?.code || "",
      description: initialValues?.description || "",
      isActive: initialValues?.isActive ?? true,
    });
    form.clearErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <form
      id="icd-code-form"
      noValidate
      onSubmit={form.onSubmit(onSubmit, () => scrollToFirstError(form))}
    >
      <Stack gap="md">
        <TextInput
          label="ICD Code"
          placeholder="e.g., K63.5, C18.2"
          withAsterisk
          {...form.getInputProps("code")}
        />

        <Textarea
          label="Description"
          placeholder="Enter ICD code description"
          minRows={3}
          withAsterisk
          {...form.getInputProps("description")}
        />

        <Switch
          label="Active"
          description="Inactive codes will not be available for selection"
          {...form.getInputProps("isActive", { type: "checkbox" })}
        />
      </Stack>
    </form>
  );
}
